// Theme configuration for Jacky Circuit
// This file centralizes all color values used throughout the application

export const colors = {
  // Primary brand colors (default: rose)
  primary: {
    50: '#fff1f2',  // Lightest rose - Almost white with slight rose tint
    100: '#ffe4e6', // Very light rose - Subtle background highlight
    200: '#fecdd3', // Light rose - Soft background or hover state
    300: '#fda4af', // Medium-light rose - Borders or secondary elements
    400: '#fb7185', // Medium rose - Prominent UI elements
    500: '#f43f5e',  // Main brand color (rose-500) - Primary buttons, key accents
    600: '#e11d48',  // Darker variant (rose-600) - Hover states, important actions
    700: '#be123c',  // Even darker (rose-700) - Active states, strong emphasis
    800: '#9f1239', // Very dark rose - Strong contrast elements
    900: '#881337', // Extremely dark rose - Deep background accents
    950: '#4c0519', // Nearly black rose - Darkest variant for special cases
  },
  
  // Background colors
  background: {
    primary: '#000000',    // Main background (black) - Page background
    secondary: '#18181b',  // Secondary background (zinc-900) - Cards, panels
    tertiary: '#27272a',   // Tertiary background (zinc-800) - Form elements, nested containers
  },
  
  // Text colors
  text: {
    primary: '#ffffff',    // Primary text (white) - Main content text
    secondary: '#a1a1aa',  // Secondary text (zinc-400) - Less important text, descriptions
    tertiary: '#71717a',   // Tertiary text (zinc-500) - Placeholders, disabled text
  },
  
  // Border colors
  border: {
    primary: '#27272a',    // Primary border (zinc-800) - Card borders, dividers
    secondary: '#3f3f46',  // Secondary border (zinc-700) - Input fields, interactive elements
  },
  
  // Status colors
  status: {
    success: '#22c55e',    // Success (green-500) - Confirmations, completed actions
    warning: '#eab308',    // Warning (yellow-500) - Alerts, pending states
    error: '#ef4444',      // Error (red-500) - Error messages, destructive actions
    info: '#3b82f6',       // Info (blue-500) - Informational messages, help text
  },
};

// Theme color palettes
export const themeColors = {
  rose: {
    primary: {
      50: '#fff1f2',  // Lightest rose - Almost white with slight rose tint
      100: '#ffe4e6', // Very light rose - Subtle background highlight
      200: '#fecdd3', // Light rose - Soft background or hover state
      300: '#fda4af', // Medium-light rose - Borders or secondary elements
      400: '#fb7185', // Medium rose - Prominent UI elements
      500: '#f43f5e', // Main brand color - Primary buttons, key accents
      600: '#e11d48', // Darker variant - Hover states, important actions
      700: '#be123c', // Even darker - Active states, strong emphasis
      800: '#9f1239', // Very dark rose - Strong contrast elements
      900: '#881337', // Extremely dark rose - Deep background accents
      950: '#4c0519', // Nearly black rose - Darkest variant for special cases
    }
  },
  blue: {
    primary: {
      50: '#eff6ff',  // Lightest blue - Almost white with slight blue tint
      100: '#dbeafe', // Very light blue - Subtle background highlight
      200: '#bfdbfe', // Light blue - Soft background or hover state
      300: '#93c5fd', // Medium-light blue - Borders or secondary elements
      400: '#60a5fa', // Medium blue - Prominent UI elements
      500: '#3b82f6', // Main brand color - Primary buttons, key accents
      600: '#2563eb', // Darker variant - Hover states, important actions
      700: '#1d4ed8', // Even darker - Active states, strong emphasis
      800: '#1e40af', // Very dark blue - Strong contrast elements
      900: '#1e3a8a', // Extremely dark blue - Deep background accents
      950: '#172554', // Nearly black blue - Darkest variant for special cases
    }
  },
  green: {
    primary: {
      50: '#f0fdf4',  // Lightest green - Almost white with slight green tint
      100: '#dcfce7', // Very light green - Subtle background highlight
      200: '#bbf7d0', // Light green - Soft background or hover state
      300: '#86efac', // Medium-light green - Borders or secondary elements
      400: '#4ade80', // Medium green - Prominent UI elements
      500: '#22c55e', // Main brand color - Primary buttons, key accents
      600: '#16a34a', // Darker variant - Hover states, important actions
      700: '#15803d', // Even darker - Active states, strong emphasis
      800: '#166534', // Very dark green - Strong contrast elements
      900: '#14532d', // Extremely dark green - Deep background accents
      950: '#052e16', // Nearly black green - Darkest variant for special cases
    }
  },
  purple: {
    primary: {
      50: '#faf5ff',  // Lightest purple - Almost white with slight purple tint
      100: '#f3e8ff', // Very light purple - Subtle background highlight
      200: '#e9d5ff', // Light purple - Soft background or hover state
      300: '#d8b4fe', // Medium-light purple - Borders or secondary elements
      400: '#c084fc', // Medium purple - Prominent UI elements
      500: '#a855f7', // Main brand color - Primary buttons, key accents
      600: '#9333ea', // Darker variant - Hover states, important actions
      700: '#7e22ce', // Even darker - Active states, strong emphasis
      800: '#6b21a8', // Very dark purple - Strong contrast elements
      900: '#581c87', // Extremely dark purple - Deep background accents
      950: '#3b0764', // Nearly black purple - Darkest variant for special cases
    }
  },
  amber: {
    primary: {
      50: '#fffbeb',  // Lightest amber - Almost white with slight amber tint
      100: '#fef3c7', // Very light amber - Subtle background highlight
      200: '#fde68a', // Light amber - Soft background or hover state
      300: '#fcd34d', // Medium-light amber - Borders or secondary elements
      400: '#fbbf24', // Medium amber - Prominent UI elements
      500: '#f59e0b', // Main brand color - Primary buttons, key accents
      600: '#d97706', // Darker variant - Hover states, important actions
      700: '#b45309', // Even darker - Active states, strong emphasis
      800: '#92400e', // Very dark amber - Strong contrast elements
      900: '#78350f', // Extremely dark amber - Deep background accents
      950: '#451a03', // Nearly black amber - Darkest variant for special cases
    }
  },
  teal: {
    primary: {
      50: '#f0fdfa',  // Lightest teal - Almost white with slight teal tint
      100: '#ccfbf1', // Very light teal - Subtle background highlight
      200: '#99f6e4', // Light teal - Soft background or hover state
      300: '#5eead4', // Medium-light teal - Borders or secondary elements
      400: '#2dd4bf', // Medium teal - Prominent UI elements
      500: '#14b8a6', // Main brand color - Primary buttons, key accents
      600: '#0d9488', // Darker variant - Hover states, important actions
      700: '#0f766e', // Even darker - Active states, strong emphasis
      800: '#115e59', // Very dark teal - Strong contrast elements
      900: '#134e4a', // Extremely dark teal - Deep background accents
      950: '#042f2e', // Nearly black teal - Darkest variant for special cases
    }
  }
};

// Helper functions to generate class names based on theme
export const getThemeClass = (type: string, variant: string = ''): string => {
  switch (type) {
    case 'bg':
      if (variant.startsWith('primary-')) {
        const shade = variant.split('-')[1];
        return `bg-primary-${shade}`;
      }
      if (variant === 'primary') return 'bg-primary-500';
      if (variant === 'secondary') return 'bg-background-secondary';
      if (variant === 'tertiary') return 'bg-background-tertiary';
      break;
    case 'text':
      if (variant.startsWith('primary-')) {
        const shade = variant.split('-')[1];
        return `text-primary-${shade}`;
      }
      if (variant === 'primary') return 'text-text-primary';
      if (variant === 'secondary') return 'text-text-secondary';
      if (variant === 'tertiary') return 'text-text-tertiary';
      break;
    case 'border':
      if (variant.startsWith('primary-')) {
        const shade = variant.split('-')[1];
        return `border-primary-${shade}`;
      }
      if (variant === 'primary') return 'border-border-primary';
      if (variant === 'secondary') return 'border-border-secondary';
      break;
    case 'hover':
      if (variant.startsWith('bg-primary-')) {
        const shade = variant.split('-')[2];
        const nextShade = parseInt(shade) + 100;
        return `hover:bg-primary-${nextShade}`;
      }
      if (variant === 'bg-primary') return 'hover:bg-primary-600';
      if (variant === 'text-primary') return 'hover:text-primary-400';
      break;
    default:
      return '';
  }
  return '';
};